import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ILead } from 'src/interfaces/lead';
import { ASSISTANT_INTENT, CHAT_SCOPE } from 'src/contexts/assistant/constants';
import { TMessageActionsProps } from 'src/modules/assistant/types/actionDraft';
import { buildRecommendationDraft } from 'src/modules/assistant/helpers/buildRecommendationDraft';
import { isRecommendationStale } from 'src/modules/assistant/helpers/recommendationActions';
import { useActionExecutor } from 'src/modules/assistant/hooks/useActionExecutor';
import { TLeadChannelConversation } from 'src/modules/conversations/api';
import { TSelectedLead, TChatScope } from 'src/contexts/assistant/types';
import {
    TActionDraft,
    TRecommendation,
    TRecommendationOption,
} from 'src/modules/assistant/types/message';

interface UseMessageActionsParams {
    channel: string | null;
    channelConversation: TLeadChannelConversation;
    loadedLead: ILead | null;
    selectedLead: TSelectedLead | null;
    scope: TChatScope;
    leadId: string | null;
    requestDisabled: boolean;
    getAvailableActions: (
        recommendation: TRecommendation
    ) => TRecommendationOption[];
    rateActionSuggestion: (requestId: string) => Promise<boolean>;
    onRecalculate: () => void;
    onContinue: () => void;
}

/**
 * Builds what the message list needs to show recommendations and action drafts:
 * which options are offered, the drafts the user opened and the bound draft callbacks.
 */
export const useMessageActions = ({
    channel,
    channelConversation,
    loadedLead,
    selectedLead,
    scope,
    leadId,
    requestDisabled,
    getAvailableActions,
    rateActionSuggestion,
    onRecalculate,
    onContinue,
}: UseMessageActionsParams): TMessageActionsProps => {
    const { t } = useTranslation();
    const { execute, cancel } = useActionExecutor({
        leadId,
        channelConversation,
    });
    const [recommendationDrafts, setRecommendationDrafts] = useState<
        Record<string, TActionDraft>
    >({});

    const onSelectOption = (
        intent: string,
        recommendation: TRecommendation
    ) => {
        if (!loadedLead?.agent || intent === ASSISTANT_INTENT.ASSIGN) return;
        const draft = buildRecommendationDraft(
            intent,
            recommendation,
            loadedLead.email,
            t
        );
        if (draft)
            setRecommendationDrafts((current) => ({
                ...current,
                [recommendation.requestId]: draft,
            }));
    };

    return {
        channel,
        canExecuteDrafts: Boolean(loadedLead),
        showRecommendationActions: (recommendation) =>
            selectedLead?.id === recommendation.leadId &&
            scope === CHAT_SCOPE.LEAD,
        isRecommendationStale: (recommendation) =>
            isRecommendationStale(recommendation, selectedLead?.updatedAt),
        recalculateDisabled: requestDisabled,
        recommendationDrafts,
        getOptions: getAvailableActions,
        onRecalculate,
        onSelectOption,
        getDraftHandlers: (requestId) => ({
            onExecute: (draft) => execute(draft, requestId),
            onCancelExecution: cancel,
            onLike: () => rateActionSuggestion(requestId),
            onContinue,
        }),
    };
};
